import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Divider } from 'src/components/Divider';
import { Loader } from 'src/components/Loader';
import { PaymentMethodForm } from 'src/components/PaymentMethodForm';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Invoice } from 'src/models/payment/_types';
import {
  fetchInvoices,
  isPayableOpenInvoice,
  pastDueInvoicesQueryKey,
  payOpenInvoicesWithPaymentMethod,
} from 'src/models/payment/payment';
import { createPaymentMethod } from 'src/models/payment-methods/payment-methods';
import { fetchUserById } from 'src/models/user/user';
import { Identifier } from 'src/types/utilityTypes';
import { formatCurrency } from 'src/utils/numberFormatter';
import toast from 'src/utils/toast';

function getInvoicePrimaryLabel(invoice: Invoice) {
  return invoice.itemName || invoice.description || invoice.name;
}

function formatAmount(amount: number, currency: string) {
  return formatCurrency(amount, { currency: currency.toUpperCase() });
}

export interface PayBalanceContentProps {
  /** Member whose past-due balance is being paid. */
  userId: Identifier;
  /**
   * Called after invoices are successfully charged.
   * Wrapper decides what "success" means — close webview, advance check-in, etc.
   */
  onPaymentSuccess: () => void;
  /** Called when the user dismisses without paying (e.g. "Not you?" link). */
  onCancel?: () => void;
  /**
   * Show the "Logged in as <email>" header for member-self-pay flows.
   * Trainer-driven flows (1737) should leave this false.
   */
  showMyAccount?: boolean;
}

export function PayBalanceContent({
  userId,
  onPaymentSuccess,
  onCancel,
  showMyAccount = false,
}: PayBalanceContentProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'payBalance' });
  const queryClient = useQueryClient();

  const { data: memberUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => fetchUserById(userId),
    enabled: !!userId,
  });

  const { data: invoicePayload, isLoading: isLoadingInvoices } = useQuery({
    queryKey: pastDueInvoicesQueryKey(userId),
    queryFn: () =>
      fetchInvoices({
        userId,
        per: 100,
        filters: { pastDue: true },
      }),
    enabled: !!userId,
  });

  const invoices: Invoice[] = invoicePayload?.invoices ?? [];
  const payableInvoices = invoices.filter(isPayableOpenInvoice);
  const primaryCurrency =
    payableInvoices[0]?.currency ?? invoices[0]?.currency ?? 'usd';

  const totalAmountDue = payableInvoices.reduce(
    (acc, invoice) => acc + (invoice.amountDue ?? 0),
    0
  );
  const formattedTotal = formatAmount(totalAmountDue, primaryCurrency);

  const payMutation = useMutation({
    // Stripe confirmSetup result. The PM has just been created on Stripe's side;
    // we need to register it with our backend (createPaymentMethod) so it gets a
    // local id usable by `payOpenInvoicesWithPaymentMethod`, then charge each
    // open invoice with it.
    mutationFn: async (result: any) => {
      const setupIntent = result?.setupIntent;
      const paymentMethodType = setupIntent?.payment_method?.type;
      if (!setupIntent?.id || !paymentMethodType) {
        throw new Error(t('paymentError'));
      }
      const newPm = await createPaymentMethod({
        token: setupIntent.id,
        userId: memberUser?.id ?? userId,
        paymentMethodType,
        status:
          setupIntent.status === 'requires_action' ? 'unverified' : 'verified',
        default: false,
      });
      await payOpenInvoicesWithPaymentMethod({
        invoices,
        paymentMethodId: newPm.id,
      });
    },
    onSuccess: () => {
      toast.success(t('paymentSuccess'));
      queryClient.invalidateQueries({
        queryKey: pastDueInvoicesQueryKey(userId),
      });
      queryClient.invalidateQueries({ queryKey: ['checkinBlockers'] });
      queryClient.invalidateQueries({
        queryKey: ['paymentMethods', userId],
      });
      queryClient.invalidateQueries({
        queryKey: ['paymentMethods', 'accessible', userId],
      });
      onPaymentSuccess();
    },
    onError: (error: Error) => {
      toast.error(error.message || t('paymentError'));
    },
  });

  if (isLoadingUser || isLoadingInvoices || !memberUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8,
        }}
      >
        <Loader size="large" variant="circular" />
      </Box>
    );
  }

  const hasPayable = payableInvoices.length > 0;

  return (
    <Stack
      spacing={3}
      sx={{
        p: 2,
        // Leave room for the fixed Pay Now footer so the last fields aren't
        // hidden behind it.
        pb: hasPayable ? 12 : 2,
      }}
    >
      {showMyAccount && (
        <Stack spacing={1}>
          <Typography variant="h6">{t('myAccount')}</Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            {memberUser.email && (
              <Typography variant="body2">
                {t('loggedInAs', { email: memberUser.email })}
              </Typography>
            )}
            {onCancel && (
              <Box
                component="button"
                type="button"
                onClick={onCancel}
                sx={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'success.main',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  p: 0,
                  flexShrink: 0,
                }}
              >
                {t('notYou')}
              </Box>
            )}
          </Stack>
        </Stack>
      )}

      <Stack spacing={1.5}>
        <Typography variant="h6">{t('summary')}</Typography>
        {payableInvoices.map((invoice) => (
          <Stack
            key={invoice.id}
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography variant="body1">
                {getInvoicePrimaryLabel(invoice)}
              </Typography>
              <Typography variant="caption" color="error.main">
                {t('pastDue')}
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ flexShrink: 0 }}>
              {formatAmount(
                invoice.amountDue ?? 0,
                (invoice.currency ?? primaryCurrency).toUpperCase()
              )}
            </Typography>
          </Stack>
        ))}

        {!hasPayable && (
          <Typography variant="body2" color="text.secondary">
            {t('noPayableBalance')}
          </Typography>
        )}

        {hasPayable && (
          <>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1">{t('totalDue')}</Typography>
              <Typography variant="body1">{formattedTotal}</Typography>
            </Stack>
          </>
        )}
      </Stack>

      {hasPayable && (
        <Stack spacing={1.5}>
          <Typography variant="h6">{t('paymentMethod')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('secureNote')}
          </Typography>
          <Box
            sx={{
              opacity: payMutation.isPending ? 0.6 : 1,
              pointerEvents: payMutation.isPending ? 'none' : 'auto',
            }}
          >
            <PaymentMethodForm
              user={memberUser}
              handlePaymentMethodAdded={(result) => payMutation.mutate(result)}
              billingDetails="never"
              buttonText={t('payNow', { amount: formattedTotal })}
              fixedFooter
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
}

export default PayBalanceContent;
